import { useGlobalLoading } from '@frontends/feature-tv-parts';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { PostMessageListener } from '../app/utils/postMessageListener';
import { AuthWrapper } from '../components/authWrapper';
import { $axios, useAxiosInterceptors } from '../hooks/useAxiosInterceptors';
import { useEntityConfig } from '../hooks/useEntityConfig';
import { EAuthState } from '../types';

export interface IAuth {
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  token: string | null;
}

export const Auth = createContext<IAuth>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- placeholder default; real implementation is provided by AuthProvider
  logout: async () => {},
  token: null,
  isAuthenticated: false,
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const config = useEntityConfig();
  const [token, setToken] = useState<string | null>(null);
  const [authState, setAuthState] = useState<EAuthState>(EAuthState.INITIALIZING);
  const [template, setTemplate] = useState<ReactNode | null>(<></>);

  useGlobalLoading(authState === EAuthState.INITIALIZING);

  useAxiosInterceptors(token, setToken, setAuthState);

  const validate = async (): Promise<void> => {
    try {
      await $axios.get(`${config.VITE_URL_ID_BACKEND}/api/v1/auth/validate`);
      setAuthState(EAuthState.AUTHENTICATED);
    } catch {
      setAuthState(EAuthState.UNAUTHENTICATED);
    }
  };

  useEffect(() => {
    void validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run validate() once on mount only; validate is recreated every render
  }, []);

  const logout = async (): Promise<void> => {
    try {
      await $axios.delete(`${config.VITE_URL_ID_BACKEND}/api/v1/auth/logout`);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setToken(null);
      setAuthState(EAuthState.UNAUTHENTICATED);
      delete $axios.defaults.headers.common.Authorization;
    }
  };

  useEffect(() => {
    if (authState === EAuthState.INITIALIZING) {
      setTemplate(<></>);
    } else if (authState === EAuthState.UNAUTHENTICATED || authState === EAuthState.ERROR) {
      setTemplate(<AuthWrapper />);
    } else if (authState === EAuthState.AUTHENTICATED) {
      setTemplate(<>{children}</>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  return (
    <>
      <PostMessageListener
        onSuccessLogin={() => {
          void validate();
        }}
      />
      <Auth.Provider
        value={{
          logout,
          token,
          isAuthenticated: authState === EAuthState.AUTHENTICATED,
        }}
      >
        {template}
      </Auth.Provider>
    </>
  );
};
